import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Select, Radio, Space, Card, DatePicker } from 'antd';
import { SwapOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const PERIOD_TYPES = [
  { label: 'Month', value: 'month' },
  { label: 'Quarter', value: 'quarter' },
  { label: 'YTD', value: 'ytd' },
];

// Opt-in per page. A custom range is only meaningful where the caller can add
// months together across fiscal years, and most pages here cannot.
const CUSTOM_TYPE = { label: 'Custom', value: 'custom' };

const monthKey = (d) => `${d.year()}-${String(d.month() + 1).padStart(2, '0')}`;
const parseMonthKey = (v) => {
  const [y, m] = String(v || '').split('-').map(Number);
  return (y && m) ? dayjs().year(y).month(m - 1).startOf('month') : null;
};

// Fiscal year: July 1 → June 30
function getFiscalYear(date = dayjs()) {
  return date.month() >= 6 ? date.year() : date.year() - 1;
}

function generateMonthOptions(fiscalYear) {
  const options = [];
  for (let i = 0; i < 12; i++) {
    const month = (6 + i) % 12; // Start from July (6)
    const year = month >= 6 ? fiscalYear : fiscalYear + 1;
    const d = dayjs().year(year).month(month).startOf('month');
    options.push({
      label: d.format('MMM YYYY'),
      value: `${year}-${String(month + 1).padStart(2, '0')}`,
    });
  }
  return options;
}

function generateQuarterOptions(fiscalYear) {
  return [
    { label: `Q1 (Jul-Sep ${fiscalYear})`, value: '1' },
    { label: `Q2 (Oct-Dec ${fiscalYear})`, value: '2' },
    { label: `Q3 (Jan-Mar ${fiscalYear + 1})`, value: '3' },
    { label: `Q4 (Apr-Jun ${fiscalYear + 1})`, value: '4' },
  ];
}

function generateFiscalYearOptions() {
  const currentFY = getFiscalYear();
  const options = [];
  for (let fy = currentFY; fy >= currentFY - 4; fy--) {
    options.push({
      label: `FY${fy}/${fy + 1}`,
      value: fy,
    });
  }
  return options;
}

function computeDateRange(periodType, periodValue, fiscalYear) {
  if (periodType === 'month') {
    const [year, month] = periodValue.split('-').map(Number);
    const start = dayjs().year(year).month(month - 1).startOf('month');
    const end = start.endOf('month');
    return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') };
  }

  if (periodType === 'quarter') {
    const q = parseInt(periodValue, 10);
    const quarterMonths = { 1: [6, 8], 2: [9, 11], 3: [0, 2], 4: [3, 5] };
    const [startMonth, endMonth] = quarterMonths[q];
    const startYear = startMonth >= 6 ? fiscalYear : fiscalYear + 1;
    const endYear = endMonth >= 6 ? fiscalYear : fiscalYear + 1;
    const start = dayjs().year(startYear).month(startMonth).startOf('month');
    const end = dayjs().year(endYear).month(endMonth).endOf('month');
    return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') };
  }

  if (periodType === 'custom') {
    // "YYYY-MM:YYYY-MM". Absolute months, because the point of this mode is that
    // the range need not sit inside one fiscal year.
    const [from, to] = String(periodValue || '').split(':');
    const a = parseMonthKey(from);
    const b = parseMonthKey(to) || a;
    if (!a || !b) return { start: null, end: null };
    const lo = a.isAfter(b) ? b : a;
    const hi = a.isAfter(b) ? a : b;
    return { start: lo.startOf('month').format('YYYY-MM-DD'), end: hi.endOf('month').format('YYYY-MM-DD') };
  }

  if (periodType === 'ytd') {
    const start = dayjs().year(fiscalYear).month(6).startOf('month'); // July 1
    const fyEnd = dayjs().year(fiscalYear + 1).month(5).endOf('month'); // June 30
    const today = dayjs().endOf('month');
    // Cap YTD to the selected fiscal year — picking a past FY should
    // return that whole FY, not just Q1 aligned with today's month.
    const end = today.isAfter(fyEnd) ? fyEnd : today;
    return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') };
  }

  return { start: null, end: null };
}

export default function PeriodToolbar({
  onChange, defaultPreviousMonth, allowedFiscalYears, hideComparison, allowCustom,
  // Drop the fiscal-year Select and carry the year inside each period option
  // instead. A page whose default is a custom range does not need a year picker:
  // the months already say which workbooks to read, and two controls that must
  // agree are two controls that can disagree.
  hideFiscalYear,
  // Rendered where the year Select was. Monthly Finance puts its entity picker
  // there, which is the choice that actually belongs beside the period.
  leadingControl,
  // Rendered after the period controls — the resolved period, so it sits with
  // the thing that set it rather than in a card of its own.
  trailingContent,
  // Open on a custom range of this many months, ending last month.
  defaultCustomMonths,
}) {
  const now = dayjs();
  const defaultMonth = defaultPreviousMonth ? now.subtract(1, 'month') : now;
  const currentFY = getFiscalYear(defaultMonth);

  const [fiscalYear, setFiscalYear] = useState(currentFY);
  const [periodType, setPeriodType] = useState('month');
  const [periodValue, setPeriodValue] = useState(
    `${defaultMonth.year()}-${String(defaultMonth.month() + 1).padStart(2, '0')}`
  );
  const [comparison, setComparison] = useState(false);

  /**
   * With no year Select, each option has to carry its own year.
   *
   * Month values are already absolute (YYYY-MM) so they only need widening to
   * every available year. Quarter and YTD were fiscal-year-relative — '1'..'4'
   * and 'ytd' — so their values gain the year: '2025:1' and 'ytd:2025'. The
   * fiscal year is then read back off the selection rather than held separately.
   */
  const years = useMemo(() => {
    const all = (allowedFiscalYears && allowedFiscalYears.length)
      ? [...allowedFiscalYears]
      : generateFiscalYearOptions().map((o) => o.value);
    return all.sort((a, b) => b - a);
  }, [allowedFiscalYears]);

  const monthOptions = useMemo(() => {
    if (!hideFiscalYear) return generateMonthOptions(fiscalYear);
    return years.flatMap((fy) => generateMonthOptions(fy)).reverse();
  }, [hideFiscalYear, years, fiscalYear]);

  const quarterOptions = useMemo(() => {
    if (!hideFiscalYear) return generateQuarterOptions(fiscalYear);
    return years.flatMap((fy) => generateQuarterOptions(fy).map((o) => ({
      label: `${o.label.split(' (')[0]} FY${String(fy).slice(2)}/${String(fy + 1).slice(2)}`,
      value: `${fy}:${o.value}`,
    })));
  }, [hideFiscalYear, years, fiscalYear]);

  const ytdOptions = useMemo(() => years.map((fy) => ({
    label: `FY${String(fy).slice(2)}/${String(fy + 1).slice(2)} to date`,
    value: `ytd:${fy}`,
  })), [years]);
  const periodTypeOptions = useMemo(
    () => (allowCustom ? [...PERIOD_TYPES, CUSTOM_TYPE] : PERIOD_TYPES),
    [allowCustom],
  );

  /**
   * The months a custom range may reach, bounded by the fiscal years the caller
   * can actually serve — each is a separate workbook, so a month outside them
   * would come back empty and read as a month that cost nothing.
   */
  const customBounds = useMemo(() => {
    if (!allowedFiscalYears || !allowedFiscalYears.length) return null;
    const years = [...allowedFiscalYears].sort((a, b) => a - b);
    return {
      first: dayjs().year(years[0]).month(6).startOf('month'),
      last: dayjs().year(years[years.length - 1] + 1).month(5).endOf('month'),
    };
  }, [allowedFiscalYears]);

  const fyOptions = useMemo(() => {
    const all = generateFiscalYearOptions();
    return allowedFiscalYears ? all.filter(o => allowedFiscalYears.includes(o.value)) : all;
  }, [allowedFiscalYears]);

  // Fire onChange on mount with initial values
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;

    // A rolling window ending last month, which is the last month with complete
    // figures — this month is still being posted.
    //
    // defaultPreviousMonth wins when both are given. They are contradictory
    // instructions — open on one month, open on a span of them — and this
    // branch used to take the span silently, so a page asking for last month
    // got a twelve-month custom range and no sign of why.
    if (allowCustom && defaultCustomMonths > 0 && !defaultPreviousMonth) {
      const to = now.subtract(1, 'month').startOf('month');
      const from = to.subtract(defaultCustomMonths - 1, 'month');
      const pv = `${monthKey(from)}:${monthKey(to)}`;
      setPeriodType('custom');
      setPeriodValue(pv);
      const fy = to.month() >= 6 ? to.year() : to.year() - 1;
      const range = computeDateRange('custom', pv, fy);
      onChange?.({ periodType: 'custom', periodValue: pv, fiscalYear: fy, comparison, ...range });
      return;
    }

    const range = computeDateRange(periodType, periodValue, fiscalYear);
    onChange?.({ periodType, periodValue, fiscalYear, comparison, ...range });
  }, []);

  /**
   * The fiscal year a selection implies, when there is no year Select.
   *
   * One source of truth: whatever is chosen decides the year, so the two can
   * never drift apart.
   */
  const fyFromValue = useCallback((pt, pv, fallback) => {
    if (!hideFiscalYear) return fallback;
    if (pt === 'month') {
      const [y, m] = String(pv || '').split('-').map(Number);
      return (y && m) ? (m >= 7 ? y : y - 1) : fallback;
    }
    if (pt === 'quarter') {
      const [fy] = String(pv || '').split(':').map(Number);
      return fy || fallback;
    }
    if (pt === 'ytd') {
      const fy = Number(String(pv || '').split(':')[1]);
      return fy || fallback;
    }
    if (pt === 'custom') {
      // The range may straddle years, so the end month decides — that is the
      // year the figures are being read up to.
      const to = String(pv || '').split(':')[1];
      const [y, m] = String(to || '').split('-').map(Number);
      return (y && m) ? (m >= 7 ? y : y - 1) : fallback;
    }
    return fallback;
  }, [hideFiscalYear]);

  const fireChange = useCallback((updates = {}) => {
    const pt = updates.periodType || periodType;
    const pv = updates.periodValue || periodValue;
    const fy = fyFromValue(pt, pv, updates.fiscalYear ?? fiscalYear);
    const cmp = updates.comparison ?? comparison;

    // Quarter values carry their year for the picker's sake; the server still
    // wants the bare quarter number.
    const serverValue = (hideFiscalYear && pt === 'quarter')
      ? String(pv).split(':')[1]
      : (hideFiscalYear && pt === 'ytd') ? 'ytd' : pv;

    const range = computeDateRange(pt, serverValue, fy);

    onChange?.({
      periodType: pt,
      periodValue: serverValue,
      fiscalYear: fy,
      comparison: cmp,
      ...range,
    });
  }, [periodType, periodValue, fiscalYear, comparison, onChange, fyFromValue, hideFiscalYear]);

  // Which month to select for a given fiscal year. Prefers the caller's default
  // month (honouring defaultPreviousMonth) when it falls inside that FY, so the
  // current FY lands on a closed month; otherwise the FY's last month (June).
  // Keeps the month Select's value always present in its own options.
  const monthValueForFy = useCallback((fy) => {
    const d = getFiscalYear(defaultMonth) === fy
      ? defaultMonth
      : dayjs().year(fy + 1).month(5).startOf('month'); // Jun = last month of FY
    return `${d.year()}-${String(d.month() + 1).padStart(2, '0')}`;
  }, [defaultMonth]);

  const handlePeriodTypeChange = (e) => {
    const pt = e.target.value;
    setPeriodType(pt);

    let defaultValue;
    if (pt === 'month') {
      defaultValue = monthValueForFy(fiscalYear);
    } else if (pt === 'quarter') {
      const m = now.month();
      let q;
      if (m >= 6 && m <= 8) q = '1';
      else if (m >= 9 && m <= 11) q = '2';
      else if (m >= 0 && m <= 2) q = '3';
      else q = '4';
      defaultValue = hideFiscalYear ? `${fiscalYear}:${q}` : q;
    } else if (pt === 'custom') {
      // Opens on the fiscal year to date, which is the range somebody most often
      // wants before they narrow it, and is already a valid selection.
      const from = dayjs().year(fiscalYear).month(6).startOf('month');
      const to = defaultMonth.startOf('month');
      defaultValue = `${monthKey(from.isAfter(to) ? to : from)}:${monthKey(to)}`;
    } else {
      defaultValue = hideFiscalYear ? `ytd:${fiscalYear}` : 'ytd';
    }

    setPeriodValue(defaultValue);
    fireChange({ periodType: pt, periodValue: defaultValue });
  };

  const handlePeriodValueChange = (value) => {
    setPeriodValue(value);
    fireChange({ periodValue: value });
  };

  const handleFiscalYearChange = (value) => {
    setFiscalYear(value);
    // Month values are absolute (YYYY-MM), so they don't survive an FY change —
    // re-pick one inside the new FY. Quarter ('1'..'4') and YTD are FY-relative
    // and stay valid as-is.
    if (periodType === 'month') {
      const mv = monthValueForFy(value);
      setPeriodValue(mv);
      fireChange({ fiscalYear: value, periodValue: mv });
      return;
    }
    fireChange({ fiscalYear: value });
  };

  // Keep the selected FY inside allowedFiscalYears. The default is derived from
  // today's date, so once a new FY starts it can fall outside a restricted list
  // (which renders an empty-looking Select and fetches a year the caller can't
  // serve). Snap to the newest allowed year that isn't in the future, else the
  // newest allowed year. Also covers allowedFiscalYears arriving async.
  useEffect(() => {
    if (!allowedFiscalYears || allowedFiscalYears.length === 0) return;
    if (allowedFiscalYears.includes(fiscalYear)) return;
    const sorted = [...allowedFiscalYears].sort((a, b) => b - a);
    const target = sorted.find(y => y <= fiscalYear) ?? sorted[0];
    setFiscalYear(target);
    if (periodType === 'month') {
      const mv = monthValueForFy(target);
      setPeriodValue(mv);
      fireChange({ fiscalYear: target, periodValue: mv });
      return;
    }
    fireChange({ fiscalYear: target });
  }, [allowedFiscalYears, fiscalYear, periodType, monthValueForFy, fireChange]);

  const handleComparisonToggle = (e) => {
    const cmp = e.target.value === 'compare';
    setComparison(cmp);
    fireChange({ comparison: cmp });
  };

  return (
    <Card
      size="small"
      className="period-toolbar"
      style={{ marginBottom: 16 }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <div className="period-toolbar-row">
        <Space size="small">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          {hideFiscalYear
            ? leadingControl
            : (
              <Select
                value={fiscalYear}
                onChange={handleFiscalYearChange}
                options={fyOptions}
                style={{ width: 130 }}
                size="small"
                // A custom range is absolute and may span two fiscal years, so
                // the year picker has nothing to say about it.
                disabled={periodType === 'custom'}
              />
            )}
        </Space>

        <Radio.Group
          value={periodType}
          onChange={handlePeriodTypeChange}
          optionType="button"
          buttonStyle="solid"
          size="small"
          options={periodTypeOptions}
        />

        {periodType === 'month' && (
          <Select
            value={periodValue}
            onChange={handlePeriodValueChange}
            options={monthOptions}
            style={{ width: 140 }}
            size="small"
          />
        )}

        {periodType === 'quarter' && (
          <Select
            value={periodValue}
            onChange={handlePeriodValueChange}
            options={quarterOptions}
            style={{ width: hideFiscalYear ? 180 : 200 }}
            size="small"
          />
        )}

        {hideFiscalYear && periodType === 'ytd' && (
          <Select
            value={periodValue}
            onChange={handlePeriodValueChange}
            options={ytdOptions}
            style={{ width: 170 }}
            size="small"
          />
        )}

        {periodType === 'custom' && (() => {
          const [from, to] = String(periodValue || '').split(':');
          const a = parseMonthKey(from);
          const b = parseMonthKey(to);
          const outOfBounds = (d) => !!customBounds
            && (d.isBefore(customBounds.first, 'month') || d.isAfter(customBounds.last, 'month'));
          return (
            <Space size={6}>
              <DatePicker
                picker="month" size="small" allowClear={false} style={{ width: 120 }}
                value={a}
                placeholder="From"
                // The end cannot precede the start, and disabling is kinder than
                // silently swapping them under the reader.
                disabledDate={(d) => outOfBounds(d) || (b && d.isAfter(b, 'month'))}
                onChange={(d) => d && handlePeriodValueChange(`${monthKey(d)}:${to || monthKey(d)}`)}
              />
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>to</span>
              <DatePicker
                picker="month" size="small" allowClear={false} style={{ width: 120 }}
                value={b}
                placeholder="To"
                disabledDate={(d) => outOfBounds(d) || (a && d.isBefore(a, 'month'))}
                onChange={(d) => d && handlePeriodValueChange(`${from || monthKey(d)}:${monthKey(d)}`)}
              />
            </Space>
          );
        })()}

        {trailingContent}

        {!hideComparison && (
          <Radio.Group
            value={comparison ? 'compare' : 'single'}
            onChange={handleComparisonToggle}
            optionType="button"
            size="small"
          >
            <Radio.Button value="single">Single Period</Radio.Button>
            <Radio.Button value="compare">
              <SwapOutlined /> Compare Last 3
            </Radio.Button>
          </Radio.Group>
        )}
      </div>
    </Card>
  );
}
